import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "create_user",       label: "Create User" },
  { value: "update_user",       label: "Update User" },
  { value: "change_reporting",  label: "Change Reporting" },
  { value: "bulk_reassign",     label: "Bulk Reassign" },
  { value: "move_manager",      label: "Move Manager" },
  { value: "suspend",           label: "Suspend" },
  { value: "revoke",            label: "Revoke" },
  { value: "reactivate",        label: "Reactivate" },
];

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "1", label: "Super Admin" },
  { value: "2", label: "Admin" },
  { value: "3", label: "Manager" },
  { value: "4", label: "Team Lead" },
  { value: "5", label: "Agent" },
];

function formatAction(str) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function userName(u) {
  if (!u) return "—";
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—";
}

export default function OrgAuditLog() {
  const [rows, setRows]   = useState([]);
  const [meta, setMeta]   = useState({ total: 0, totalPages: 1 });
  const [params, setParams] = useState({ page: 1, limit: 10, search: "", action_type: "", role_id: "" });

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/admin-users/org-audit", { params })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.logs || []);
        setMeta(res.data?.meta || { total: 0, totalPages: 1 });
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.message || "Failed to load audit log");
      });
    return () => { cancelled = true; };
  }, [params]);

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold">Organisation Audit Log</h1>
        <p className="text-sm text-gray-500 mt-1">Track all organisation actions — who performed them, on whom, and why.</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mb-4">
        <input
          type="text"
          placeholder="Search by name…"
          value={params.search}
          className="rounded-xl border px-3 py-2 text-sm w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-gray-300"
          onChange={(e) => setParams((p) => ({ ...p, search: e.target.value, page: 1 }))}
        />
        <select
          value={params.action_type}
          className="rounded-xl border px-3 py-2 text-sm w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-gray-300"
          onChange={(e) => setParams((p) => ({ ...p, action_type: e.target.value, page: 1 }))}
        >
          {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={params.role_id}
          className="rounded-xl border px-3 py-2 text-sm w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-gray-300"
          onChange={(e) => setParams((p) => ({ ...p, role_id: e.target.value, page: 1 }))}
        >
          {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {(params.search || params.action_type || params.role_id) && (
          <button
            type="button"
            className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 w-full sm:w-auto"
            onClick={() => setParams({ page: 1, search: "", action_type: "", role_id: "" })}
          >
            Clear
          </button>
        )}
      </div>

      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b bg-gray-50 text-xs uppercase text-gray-500">
              <th className="py-3 px-4">Action Performed By</th>
              <th className="py-3 px-4">Action</th>
              <th className="py-3 px-4">User </th>
              <th className="py-3 px-4 whitespace-nowrap">Previous Reporting</th>
              <th className="py-3 px-4 whitespace-nowrap">Current Reporting</th>
              <th className="py-3 px-4">Reason</th>
              <th className="py-3 px-4 whitespace-nowrap">Action Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-gray-400 text-sm">No audit records found</td>
              </tr>
            )}
            {rows.map((log) => (
              <tr key={log.log_id} className="border-b last:border-0 hover:bg-gray-50">

                <td className="py-3 px-4">
                  <div className="font-medium text-gray-900">{userName(log.actor)}</div>
                  <div className="text-xs text-gray-400">{log.actor?.role?.name || "—"}</div>
                </td>
                <td className="py-3 px-4">
                  <span className="inline-block rounded-full bg-blue-50 text-blue-700 px-2.5 py-0.5 text-xs font-medium whitespace-nowrap">
                    {formatAction(log.action_type)}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="font-medium text-gray-900">{userName(log.target)}</div>
                  <div className="text-xs text-gray-400">{log.target?.role?.name || "—"}</div>
                </td>
                <td className="py-3 px-4 text-xs text-gray-600">{userName(log.prev_parent)}</td>
                <td className="py-3 px-4 text-xs text-gray-600">{userName(log.new_parent)}</td>
                <td className="py-3 px-4 text-xs text-gray-600">
                  {log.reason_category
                    ? log.reason_category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                    : "—"}
                </td>
                <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-500">
                  {new Date(log.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-gray-500 text-xs">
            Page {params.page} of {meta.totalPages} &nbsp;·&nbsp; {meta.total ?? "—"} total records
          </span>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span>Per page</span>
            <select
              value={params.limit}
              className="rounded-lg border px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-gray-300"
              onChange={(e) => setParams((p) => ({ ...p, limit: Number(e.target.value), page: 1 }))}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-xl border px-4 py-1.5 text-xs font-medium disabled:opacity-40 hover:bg-gray-50"
            disabled={params.page <= 1}
            onClick={() => setParams((p) => ({ ...p, page: p.page - 1 }))}
          >
            Prev
          </button>
          <button
            type="button"
            className="rounded-xl border px-4 py-1.5 text-xs font-medium disabled:opacity-40 hover:bg-gray-50"
            disabled={params.page >= meta.totalPages}
            onClick={() => setParams((p) => ({ ...p, page: p.page + 1 }))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
